import { defineStore } from "pinia"

import * as webUsersService from "@/services/webUsersService.js"
import { expiresAt as tokenExpiresAt } from "@/utils/jwt.js"

const STORAGE_KEY = "userSession"

// Refresh once the token has less than this left, rather than on a blind timer. The token lasts a
// week, so the old 20 minute interval rotated it about 70 times a day for no benefit - and every
// rotation was a chance for a second tab to be left holding the token it had just invalidated.
const REFRESH_WHEN_WITHIN_MS = 24 * 60 * 60 * 1000

// One refresh at a time per tab: several requests failing at once must not each start their own,
// because the first to finish invalidates the token all the others are carrying.
let refreshPromise = null

// What another tab has written, which is the authority when this tab's copy has been superseded.
const readPersistedSession = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw)?.session ?? null : null
  } catch {
    return null
  }
}

// The tab that loses a simultaneous refresh can be told no before the winner has written the
// replacement, so the write is given a moment to land rather than the session being written off.
// Half a second only ever delays a sign-out that is genuinely due.
const waitForRotation = async (previousToken, timeoutMs = 500) => {
  const deadline = Date.now() + timeoutMs

  for (;;) {
    const persisted = readPersistedSession()

    if (persisted?.refreshToken && persisted.refreshToken !== previousToken) return persisted
    if (Date.now() >= deadline) return null

    await new Promise((resolve) => setTimeout(resolve, 50))
  }
}

export const useSessionStore = defineStore("userSession", {
  state: () => ({
    session: null
  }),
  getters: {
    isLogged: (state) => state.session != null,
    roles: (state) => state.session?.roles ?? [],
    // null when the token cannot be read, which callers treat as "refresh, do not assume". Safe as
    // a getter because it derives purely from the session, which is reactive.
    expiresAt: (state) => (state.session?.token ? tokenExpiresAt(state.session.token) : null)
  },
  actions: {
    async logIn(username, password) {
      this.session = await webUsersService.login(username, password)
      return this.session
    },

    /**
     * Exchanges the token pair for a new one. Safe to call concurrently - callers share the one
     * in-flight attempt. Rejects only when the session is genuinely finished.
     */
    async refreshToken() {
      if (!this.session) return null

      if (!refreshPromise) {
        refreshPromise = this.runRefresh().finally(() => {
          refreshPromise = null
        })
      }

      return await refreshPromise
    },

    async runRefresh() {
      const used = this.session.refreshToken

      try {
        this.session = await webUsersService.refresh(this.session.token, used)
        return this.session
      } catch (error) {
        // Rotation is single use, so another tab may have spent this token between the read above
        // and the call. That tab writes the replacement to storage; adopt it and try once more
        // before treating the session as dead.
        const persisted = await waitForRotation(used)

        if (persisted) {
          this.session = await webUsersService.refresh(persisted.token, persisted.refreshToken)
          return this.session
        }

        throw error
      }
    },

    /**
     * Refreshes only when the token is close to running out.
     *
     * The comparison lives here rather than in a getter on purpose. Getters are cached against
     * their reactive dependencies and Date.now() is not one of them, so a getter that answered
     * "not yet" cached that answer until the session changed - and the only thing that changes the
     * session is a refresh, which was waiting on the answer. The periodic check could therefore
     * never fire, leaving the 401 retry as the only way a token was ever renewed.
     */
    async refreshIfNeeded() {
      if (!this.session) return null

      const expiresAt = this.expiresAt

      // An unreadable expiry means refresh rather than assume there is time left.
      if (expiresAt != null && expiresAt - Date.now() >= REFRESH_WHEN_WITHIN_MS) return null

      return await this.refreshToken()
    },

    async logOut() {
      const previous = this.session

      // Cleared first, so the UI reacts at once and nothing else attaches the dead token. The
      // logout endpoint takes the pair in its body and needs no Authorization header.
      this.session = null

      if (!previous?.token) return

      try {
        await webUsersService.logout(previous.token, previous.refreshToken)
      } catch {
        // Best effort. The local session is already gone either way, and the server drops the
        // refresh token on its own expiry.
      }
    },

    /**
     * Takes on the session another tab persisted. The plugin does not watch storage events, so
     * without this each tab keeps its own copy and the one that refreshes second presents a token
     * the first has already spent - which logged people out for having two tabs open.
     */
    adoptPersistedSession() {
      const next = readPersistedSession()

      // Identical values are ignored, because assigning would persist again and fire this back at
      // the other tab, and the two would keep answering each other forever.
      if (JSON.stringify(next) === JSON.stringify(this.session)) return

      this.session = next
    },

    hasRole(role) {
      return this.roles.includes(role)
    }
  },
  persist: {
    pick: ["session"]
  }
})
